import React, { TextareaHTMLAttributes, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  wrapperClassName?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, name, error, wrapperClassName = "mb-4", ...props }, ref) => {
    // This local ref will be attached to the textarea element.
    const localRef = useRef<HTMLTextAreaElement>(null);

    // This hook is crucial. It merges the `ref` from the parent (from `register`) 
    // with our `localRef`, so both `react-hook-form` and this component can access the textarea element.
    useImperativeHandle(ref, () => localRef.current!);

    const adjustHeight = useCallback(() => {
      if (localRef.current) {
        localRef.current.style.height = 'auto'; // Reset height to get the correct scrollHeight
        localRef.current.style.height = `${localRef.current.scrollHeight}px`; // Set the new height
      }
    }, []);

    // This effect runs when the component mounts and if the `props.value` changes.
    // This is important for setting the initial height correctly when the form is loaded with default values.
    useEffect(() => {
      // A small timeout allows the browser to render the component with its initial value before we adjust the height.
      const timer = setTimeout(() => {
        adjustHeight();
      }, 10);
      return () => clearTimeout(timer);
    }, [props.value, adjustHeight]);

    // This is our custom onChange handler.
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      // First, we adjust the height based on the new content.
      adjustHeight();
      // Then, we call the original onChange from the props (which will be from react-hook-form's `register`).
      // This ensures that the form's state is updated correctly.
      if (props.onChange) {
        props.onChange(e);
      }
    };

    return (
      <div className={wrapperClassName}>
        {label && (
          <label htmlFor={name} className="block text-sm font-medium text-neutral-dark mb-1">
            {label}
          </label>
        )}
        <textarea
          ref={localRef}
          id={name}
          name={name}
          // Default to 1 row initially, JS will expand it.
          // Using `overflow-y-hidden` prevents the scrollbar from briefly appearing during resize.
          // `resize-none` stops the user from manually resizing, as we're handling it dynamically.
          rows={1}
          className={`mt-1 block w-full px-3 py-2 border ${
            error ? 'border-red-500' : 'border-neutral-DEFAULT'
          } rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm overflow-y-hidden resize-none`}
          {...props}
          onChange={handleChange} // Use our custom handler
        />
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;